import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Heading, Text, VStack, Flex, Textarea, useToast, Link } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useRecoilValue, useRecoilState } from 'recoil';
import { userAtom } from '../state/userAtom';
import { siteContentAtom, siteContentLoadedAtom } from '../state/filterAtom';
import { FilterBar } from '../components/media/FilterBar';
import { MediaGrid } from '../components/media/MediaGrid';
import { Pagination } from '../components/media/Pagination';
import { useMedia } from '../hooks/useMedia';
import { getSiteContent, updateSiteContent } from '../api/media';

const renderTextWithLinks = (text) => {
  if (!text) return null;
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const [, linkText, url] = match;
    parts.push(
      url.startsWith('/') ? (
        <Link key={match.index} as={RouterLink} to={url} color="brand.ltRed" fontWeight="700" fontSize="0.85em" textDecoration="underline" _hover={{ color: 'brand.dkRed' }}>
          {linkText}
        </Link>
      ) : (
        <Link key={match.index} href={url} color="brand.ltRed" fontWeight="700" fontSize="0.85em" textDecoration="underline" _hover={{ color: 'brand.dkRed' }} isExternal={!url.startsWith('mailto:')}>
          {linkText}
        </Link>
      )
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 1 ? parts : text;
};

const EditableText = ({ contentKey, value, onSave, isAdmin, ...textProps }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const textareaRef = useRef(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (editing && textareaRef.current) {
      const el = textareaRef.current;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [editing, draft]);

  const handleBlur = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) {
      onSave(contentKey, trimmed);
    } else {
      setDraft(value);
    }
  };

  if (!isAdmin) {
    return (
      <Text {...textProps} whiteSpace="pre-line">
        {renderTextWithLinks(value)}
      </Text>
    );
  }

  if (editing) {
    return (
      <Textarea
        ref={textareaRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        autoFocus
        variant="unstyled"
        resize="none"
        overflow="hidden"
        p={1}
        border="1px dashed"
        borderColor="brand.ltRed"
        borderRadius="md"
        {...textProps}
      />
    );
  }

  return (
    <Text
      {...textProps}
      whiteSpace="pre-line"
      cursor="pointer"
      _hover={{ outline: '1px dashed', outlineColor: 'brand.ltRed', outlineOffset: '4px', borderRadius: 'md' }}
      onClick={() => setEditing(true)}
    >
      {renderTextWithLinks(value)}
    </Text>
  );
};

const EditableHeading = ({ contentKey, value, onSave, isAdmin, as, ...headingProps }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const handleBlur = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) {
      onSave(contentKey, trimmed);
    } else {
      setDraft(value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    }
  };

  if (!isAdmin) {
    return <Heading as={as} {...headingProps}>{value}</Heading>;
  }

  if (editing) {
    return (
      <Textarea
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        autoFocus
        variant="unstyled"
        resize="none"
        rows={1}
        p={1}
        border="1px dashed"
        borderColor="brand.ltRed"
        borderRadius="md"
        {...headingProps}
      />
    );
  }

  return (
    <Heading
      as={as}
      {...headingProps}
      cursor="pointer"
      _hover={{ outline: '1px dashed', outlineColor: 'brand.ltRed', outlineOffset: '4px', borderRadius: 'md' }}
      onClick={() => setEditing(true)}
    >
      {value}
    </Heading>
  );
};

export const HomePage = () => {
  const {
    media,
    pagination,
    loading,
    error,
    filters,
    updateFilters,
    setPage
  } = useMedia();

  const user = useRecoilValue(userAtom);
  const isAdmin = user?.role === 'admin';
  const toast = useToast();
  const [content, setContent] = useRecoilState(siteContentAtom);
  const [contentLoaded, setContentLoaded] = useRecoilState(siteContentLoadedAtom);

  useEffect(() => {
    if (contentLoaded) return;

    let cancelled = false;
    const loadContent = async () => {
      try {
        const data = await getSiteContent();
        if (!cancelled) {
          setContent((prev) => ({ ...prev, ...data }));
          setContentLoaded(true);
        }
      } catch (err) {
        if (!cancelled) console.error('Failed to load site content');
      }
    };
    loadContent();
    return () => { cancelled = true; };
  }, [contentLoaded, setContent, setContentLoaded]);

  const handleSave = useCallback(async (key, value) => {
    try {
      await updateSiteContent(key, value);
      setContent((prev) => ({ ...prev, [key]: value }));
      toast({ title: 'Content saved', status: 'success', duration: 2000 });
    } catch (err) {
      console.error('Failed to save content:', err);
      toast({ title: 'Failed to save content', description: err.response?.data?.error || err.message, status: 'error', duration: 4000 });
    }
  }, [toast, setContent]);

  return (
    <Box>
      <Box
        position="relative"
        color="brand.white"
        py={{ base: 20, md: 32 }}
        px={{ base: 4, md: 8 }}
        textAlign="center"
        backgroundImage="url('/img/bg-hero.jpg')"
        backgroundSize="cover"
        backgroundPosition="center bottom"
      >
        {contentLoaded && (
          <EditableHeading
            contentKey="hero_heading"
            value={content.hero_heading}
            onSave={handleSave}
            isAdmin={isAdmin}
            as="h1"
            fontSize={{ base: '4xl', md: '6xl', lg: '7xl' }}
            fontWeight="700"
            lineHeight="1.0"
            letterSpacing="-0.02em"
            maxW="800px"
            mx="auto"
            textShadow="2px 2px 8px rgba(0, 0, 0, 0.3)"
            color="brand.white"
          />
        )}
      </Box>

      <Box py={8} px={{ base: 4, md: 8 }} textAlign="center">
        <VStack spacing={0} maxW="900px" mx="auto">
          {contentLoaded && (
            <EditableText
              contentKey="intro_text"
              value={content.intro_text}
              onSave={handleSave}
              isAdmin={isAdmin}
              fontSize="2xl"
              color="brand.dkBlue"
              fontWeight="600"
              lineHeight="1.1"
              letterSpacing="-0.02em"
              w="100%"
            />
          )}
        </VStack>
      </Box>

      <Box pt={1} pb={4} px={{ base: 4, md: 8 }}>
        <VStack spacing={2} maxW="1400px" mx="auto" align="flex-start">
          {contentLoaded && (
            <>
              <EditableHeading
                contentKey="toolbox_heading"
                value={content.toolbox_heading}
                onSave={handleSave}
                isAdmin={isAdmin}
                as="h2"
                fontSize="xl"
                fontWeight="700"
                color="brand.dkGray2"
                letterSpacing="-0.02em"
              />
              <EditableText
                contentKey="toolbox_text"
                value={content.toolbox_text}
                onSave={handleSave}
                isAdmin={isAdmin}
                fontSize="md"
                color="brand.dkGray2"
                w="100%"
              />
            </>
          )}
        </VStack>
      </Box>

      <FilterBar filters={filters} onFilterChange={updateFilters} />

      <MediaGrid media={media} loading={loading} error={error} />

      <Pagination pagination={pagination} onPageChange={setPage} />
    </Box>
  );
};
